/**
 * Leveling Grid Component
 * 
 * Visual 5x6 grid showing items organized by:
 * - Rows: Altitude Level (5=Strategic to 1=Tactical)
 * - Columns: Item Category (6 types)
 * 
 * Altitude is computed from dimensions (time horizon, work size, impact surface).
 * Allows drag-and-drop to realign items.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { 
  Search,
  CheckCircle,
  AlertTriangle,
  Grid3x3,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { DraggableLevelingItem } from '../../../components/prioritization/leveling/LevelingItemCard';
import { 
  ALTITUDE_LABELS,
  ALTITUDE_LEVELS_ORDERED,
  validateAllLevelingConstraints,
} from '../../../lib/altitudeValidation';
import { computeAltitudeLevel, legacyAltitudeToLevel } from '../../../lib/altitudePrediction';
import { effortFieldsFromAltitudeLevel } from '../../../lib/itemToPlanningAdapter';
import { getItemEffortBandHours } from '../../../stores/prioritization/planningDefaultsStore';
import type { AltitudeLevel } from '../../../types/prioritization/itemLifecycle';
import type { CandidateLanguageType, Candidate } from '../../../types/prioritization/lifecycle';
import { CATEGORY_BADGE, CATEGORY_BADGE_FALLBACK } from '../../../lib/itemCategoryColors';
import { useVisualViewportFlags } from '../../../hooks/prioritization/useVisualViewport';

// ============================================================================
// Types
// ============================================================================

interface GridCell {
  altitudeLevel: AltitudeLevel;
  itemCategory: CandidateLanguageType | 'unset';
  items: Candidate[];
  hasViolations: boolean;
}

// ============================================================================
// Constants
// ============================================================================

// 6 item categories (the horizontal axis)
const CANDIDATE_TYPES: CandidateLanguageType[] = [
  'problem',
  'opportunity',
  'risk',
  'solution',
  'commitment',
  'investment',
];

const CANDIDATE_TYPE_LABELS: Record<CandidateLanguageType, string> = {
  problem: 'Problem',
  opportunity: 'Opportunity',
  risk: 'Risk',
  solution: 'Solution',
  commitment: 'Commitment',
  investment: 'Investment',
};

function getCandidateTypeColors(type: CandidateLanguageType): string {
  const badge = CATEGORY_BADGE[type] ?? CATEGORY_BADGE_FALLBACK;
  return `${badge.bg} ${badge.border}`;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the altitude level for a candidate
 * Uses altitudeLevel if set, otherwise computes from scoping inputs,
 * otherwise converts from legacy altitude label
 */
function getCandidateAltitudeLevel(candidate: Candidate): AltitudeLevel {
  // First try the new altitudeLevel field
  if (candidate.altitudeLevel) {
    return candidate.altitudeLevel;
  }
  
  // Try computing from scoping inputs
  if (candidate.altitudeScopingInputs) {
    return computeAltitudeLevel(candidate.altitudeScopingInputs);
  }
  
  // Fall back to legacy altitude conversion
  const legacyAltitude = candidate.canonicalAltitude || candidate.altitude;
  if (legacyAltitude) {
    return legacyAltitudeToLevel(legacyAltitude) || 3;
  }
  
  // Default to middle level
  return 3;
}

// ============================================================================
// Component
// ============================================================================

export function LevelingGrid() {
  const items = useItemsStore(state => state.items);
  const updateItem = useItemsStore(state => state.updateItem);
  const fetchFromSupabase = useItemsStore(state => state.fetchFromSupabase);
  const syncState = useItemsStore(state => state.syncState);
  
  // Fetch items from Supabase on mount
  useEffect(() => {
    fetchFromSupabase();
  }, [fetchFromSupabase]);
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [showViolationsOnly, setShowViolationsOnly] = useState(false);
  const [expandedCell, setExpandedCell] = useState<string | null>(null);
  const [expandedAltitude, setExpandedAltitude] = useState<AltitudeLevel | null>(5);
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);
  const { isPhone } = useVisualViewportFlags();
  
  // Filter candidates
  const filteredCandidates = useMemo(() => {
    let result = items;
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c => 
        c.title.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [items, searchQuery]);
  
  // Get violations for all candidates
  const violations = useMemo(() => {
    return validateAllLevelingConstraints(filteredCandidates);
  }, [filteredCandidates]);
  
  // Build violation map by item ID
  const violationsByItem = useMemo(() => {
    const map = new Map<string, number>();
    for (const v of violations.violations) {
      map.set(v.itemId, (map.get(v.itemId) || 0) + 1);
    }
    return map;
  }, [violations]);
  
  // Count items without a category (can't be placed in grid)
  const uncategorizedItems = useMemo(() => {
    return filteredCandidates.filter(c => !c.itemCategory || !CANDIDATE_TYPES.includes(c.itemCategory as CandidateLanguageType));
  }, [filteredCandidates]);
  
  // Count items that are categorized and can be placed in grid
  const categorizedCount = filteredCandidates.length - uncategorizedItems.length;
  
  // Build 5x6 grid data (5 altitude levels x 6 item categories)
  const gridData = useMemo(() => {
    const grid: GridCell[][] = [];
    
    // Levels ordered from highest (5) to lowest (1)
    for (const level of ALTITUDE_LEVELS_ORDERED) {
      const row: GridCell[] = [];
      
      for (const type of CANDIDATE_TYPES) {
        const items = filteredCandidates.filter(c => {
          const itemLevel = getCandidateAltitudeLevel(c);
          const itemType = c.itemCategory;
          return itemLevel === level && itemType === type;
        });
        
        const hasViolations = items.some(item => violationsByItem.has(item.id));
        
        // Apply violations filter
        if (showViolationsOnly && !hasViolations && items.length > 0) {
          row.push({
            altitudeLevel: level,
            itemCategory: type,
            items: [],
            hasViolations: false,
          });
        } else {
          row.push({
            altitudeLevel: level,
            itemCategory: type,
            items,
            hasViolations,
          });
        }
      }
      
      grid.push(row);
    }
    
    return grid;
  }, [filteredCandidates, violationsByItem, showViolationsOnly]);
  
  // Handle drag over cell
  const handleDragOver = useCallback((e: React.DragEvent, cellKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell(cellKey);
  }, []);
  
  // Handle drop on cell - updates altitudeLevel directly
  const handleDrop = useCallback((e: React.DragEvent, targetLevel: AltitudeLevel, targetType: CandidateLanguageType) => {
    e.preventDefault();
    setDragOverCell(null);
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      const { itemId } = data;
      
      const existing = useItemsStore.getState().items.find((i) => i.id === itemId);
      const hasWorkSize = !!existing?.altitudeScopingInputs?.workSize;
      const preserveEffort =
        existing?.effortSource === 'planner_override' || existing?.effortSource === 'explicit';
      const inferred =
        !hasWorkSize && !preserveEffort
          ? effortFieldsFromAltitudeLevel(targetLevel, getItemEffortBandHours())
          : null;

      updateItem(itemId, {
        altitudeLevel: targetLevel,
        itemCategory: targetType,
        altitudeLifecycleState: 'proposed',
        ...(inferred ?? {}),
      });
    } catch (err) {
      console.error('Failed to parse drop data:', err);
    }
  }, [updateItem]);
  
  // Toggle cell expansion
  const toggleCell = (cellKey: string) => {
    setExpandedCell(expandedCell === cellKey ? null : cellKey);
  };

  const toggleAltitudeSection = (level: AltitudeLevel) => {
    setExpandedAltitude(expandedAltitude === level ? null : level);
  };
  
  // Get cell key
  const getCellKey = (level: AltitudeLevel, type: CandidateLanguageType) => 
    `${level}-${type}`;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-pri)]">Leveling Grid</h1>
          <p className="text-[var(--text-sec)] mt-1">
            5 altitude levels x 6 item categories - drag items to realign
          </p>
        </div>
        
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search items..."
              className="pl-9 pr-4 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          {/* Violations filter */}
          <button
            onClick={() => setShowViolationsOnly(!showViolationsOnly)}
            className={`
              flex items-center gap-2 px-3 py-2  text-sm transition-colors
              ${showViolationsOnly 
                ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]' 
                : 'bg-[var(--hover-tint)] text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--hover-tint)]'
              }
            `}
          >
            <AlertTriangle className="h-4 w-4" />
            {showViolationsOnly ? 'Showing Violations' : 'Show Violations Only'}
          </button>
        </div>
      </div>
      
      {/* Stats bar */}
      <div className="flex items-center gap-6 p-4 bg-[var(--surface)] border border-[var(--rule)]">
        {syncState.isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 text-[var(--text-ter)] animate-spin" />
            <span className="text-sm text-[var(--text-sec)]">Loading items...</span>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Grid3x3 className="h-5 w-5 text-[var(--text-ter)]" />
              <span className="text-sm text-[var(--text-sec)]">
                <strong className="text-[var(--text-pri)]">{categorizedCount}</strong> items in 5x6 grid
                {uncategorizedItems.length > 0 && (
                  <span className="text-[var(--mustard)] ml-1">
                    ({uncategorizedItems.length} uncategorized)
                  </span>
                )}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              {violations.summary.errors > 0 ? (
                <AlertTriangle className="h-5 w-5 text-[var(--coral)]" />
              ) : violations.summary.warnings > 0 ? (
                <AlertTriangle className="h-5 w-5 text-[var(--mustard)]" />
              ) : (
                <CheckCircle className="h-5 w-5 text-[var(--green)]" />
              )}
              <span className="text-sm text-[var(--text-sec)]">
                {violations.summary.total === 0 ? (
                  <span className="text-[var(--green)] dark:text-[var(--text-ter)]">No violations</span>
                ) : (
                  <>
                    <strong className="text-[var(--coral)]">{violations.summary.errors}</strong> errors,{' '}
                    <strong className="text-[var(--mustard)]">{violations.summary.warnings}</strong> warnings
                  </>
                )}
              </span>
            </div>
          </>
        )}
      </div>
      
      {/* Grid — phone: accordion by altitude; tablet+: sticky row/column headers */}
      {isPhone ? (
        <div className="space-y-2">
          {gridData.map((row, rowIndex) => {
            const level = ALTITUDE_LEVELS_ORDERED[rowIndex];
            const rowItemCount = row.reduce((sum, cell) => sum + cell.items.length, 0);
            const isAltitudeOpen = expandedAltitude === level;

            return (
              <div
                key={level}
                className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleAltitudeSection(level)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left bg-[var(--surface)] hover:bg-[var(--hover-tint)] transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex-shrink-0">
                      {level}
                    </span>
                    <span className="text-sm font-semibold text-[var(--text-pri)] truncate">
                      {ALTITUDE_LABELS[level]}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-[var(--text-ter)]">
                      {rowItemCount} item{rowItemCount !== 1 ? 's' : ''}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-[var(--text-ter)] transition-transform ${isAltitudeOpen ? 'rotate-180' : ''}`}
                    />
                  </div>
                </button>

                {isAltitudeOpen && (
                  <div className="divide-y divide-[var(--rule)]">
                    {row.map((cell) => {
                      const cellKey = getCellKey(cell.altitudeLevel, cell.itemCategory);
                      const isDragOver = dragOverCell === cellKey;
                      const type = cell.itemCategory as CandidateLanguageType;

                      return (
                        <div
                          key={cellKey}
                          className={`p-3 transition-colors ${isDragOver ? 'bg-primary-50' : ''} ${cell.hasViolations ? 'bg-[var(--mustard-dim)]/60' : ''}`}
                          onDragOver={(e) => handleDragOver(e, cellKey)}
                          onDragLeave={() => setDragOverCell(null)}
                          onDrop={(e) => handleDrop(e, cell.altitudeLevel, type)}
                        >
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span className={`text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded border ${getCandidateTypeColors(type)}`}>
                              {CANDIDATE_TYPE_LABELS[type]}
                            </span>
                            <span className="text-xs text-[var(--text-ter)]">
                              {cell.items.length} item{cell.items.length !== 1 ? 's' : ''}
                            </span>
                          </div>

                          {cell.items.length === 0 ? (
                            <p className="text-sm text-[var(--text-ter)] text-center py-2">Drop items here</p>
                          ) : (
                            <div className="space-y-2">
                              {cell.items.map((item, index) => (
                                <DraggableLevelingItem
                                  key={item.id}
                                  item={item}
                                  index={index}
                                  hasViolations={violationsByItem.has(item.id)}
                                  violationCount={violationsByItem.get(item.id) || 0}
                                  compact
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto border border-[var(--rule)] bg-[var(--bg-raised)]">
          <table className="w-full border-collapse min-w-[960px]">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 p-2 text-left text-sm font-medium text-[var(--text-ter)] w-32 bg-[var(--surface)] border-b border-r border-[var(--rule)] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                  Level
                </th>
                {CANDIDATE_TYPES.map(type => (
                  <th
                    key={type}
                    className={`sticky top-0 z-20 p-2 text-center text-sm font-medium text-[var(--text-sec)] border-b border-[var(--rule)] min-w-[140px] ${getCandidateTypeColors(type)}`}
                  >
                    {CANDIDATE_TYPE_LABELS[type]}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {gridData.map((row, rowIndex) => {
                const level = ALTITUDE_LEVELS_ORDERED[rowIndex];

                return (
                  <tr key={level} className={rowIndex % 2 === 0 ? 'bg-[var(--bg-raised)]' : 'bg-[var(--surface)]/40'}>
                    <td className="sticky left-0 z-10 p-2 text-sm font-medium text-[var(--text-sec)] border border-[var(--rule)] bg-inherit shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                          {level}
                        </span>
                        <span>{ALTITUDE_LABELS[level]}</span>
                      </div>
                    </td>

                    {row.map((cell) => {
                      const cellKey = getCellKey(cell.altitudeLevel, cell.itemCategory);
                      const isExpanded = expandedCell === cellKey;
                      const isDragOver = dragOverCell === cellKey;

                      return (
                        <td
                          key={cellKey}
                          className={`
                            p-2 border border-[var(--rule)] align-top min-w-[140px] transition-colors
                            ${isDragOver ? 'bg-primary-50 border-primary-300' : ''}
                            ${cell.hasViolations ? 'bg-[var(--mustard-dim)]' : ''}
                          `}
                          onDragOver={(e) => handleDragOver(e, cellKey)}
                          onDragLeave={() => setDragOverCell(null)}
                          onDrop={(e) => handleDrop(e, cell.altitudeLevel, cell.itemCategory as CandidateLanguageType)}
                        >
                          {cell.items.length === 0 ? (
                            <div className="text-center text-[var(--text-ter)] text-sm py-4">
                              —
                            </div>
                          ) : (
                            <div>
                              <button
                                type="button"
                                onClick={() => toggleCell(cellKey)}
                                className={`
                                  w-full flex items-center justify-between p-2  transition-colors
                                  ${cell.hasViolations
                                    ? 'bg-[var(--mustard-dim)] hover:bg-[var(--mustard-dim)]'
                                    : 'bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)]'
                                  }
                                `}
                              >
                                <span className="font-medium text-[var(--text-pri)]">
                                  {cell.items.length}
                                </span>
                                <ChevronDown className={`h-4 w-4 text-[var(--text-ter)] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>

                              {isExpanded && (
                                <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
                                  {cell.items.map((item, index) => (
                                    <DraggableLevelingItem
                                      key={item.id}
                                      item={item}
                                      index={index}
                                      hasViolations={violationsByItem.has(item.id)}
                                      violationCount={violationsByItem.get(item.id) || 0}
                                      compact
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Uncategorized Items Section */}
      {uncategorizedItems.length > 0 && (
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4">
          <h3 className="font-medium text-[var(--mustard)] mb-3">
            Uncategorized Items ({uncategorizedItems.length})
          </h3>
          <p className="text-sm text-[var(--mustard)] mb-3">
            These items need an item category to appear in the grid. Drag them to a cell to assign both altitude level and category.
          </p>
          <div className="flex flex-wrap gap-2">
            {uncategorizedItems.slice(0, 20).map((item, index) => (
              <DraggableLevelingItem
                key={item.id}
                item={item}
                index={index}
                hasViolations={violationsByItem.has(item.id)}
                violationCount={violationsByItem.get(item.id) || 0}
                compact
              />
            ))}
            {uncategorizedItems.length > 20 && (
              <span className="inline-flex items-center px-3 py-2 bg-[var(--mustard-dim)] text-[var(--mustard)] text-sm">
                +{uncategorizedItems.length - 20} more
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Legend */}
      <div className="flex items-center gap-4 text-sm text-[var(--text-sec)]">
        <span className="font-medium">Legend:</span>
        <span className="flex items-center gap-1">
          <span className="w-4 h-4 rounded bg-[var(--mustard-dim)] border border-[var(--mustard)]" />
          Has violations
        </span>
        <span className="flex items-center gap-1">
          <span className="w-4 h-4 rounded bg-primary-50 border border-primary-300" />
          Drop target
        </span>
      </div>
    </div>
  );
}

export default LevelingGrid;
